import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { SERVER_NAME } from './config';
import axios from 'axios';
import useStyles from './sharedStyles'; 
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Checkbox, FormControlLabel, Radio, CircularProgress, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import InputAdornment from '@material-ui/core/InputAdornment';
import ClearIcon from '@material-ui/icons/Clear'; 
import SearchIcon from '@material-ui/icons/Search';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { FaTimes, FaCircle } from 'react-icons/fa';
import { futLogoIcon200 } from './config';
import { subscribeWithETH, subscribeWithE3D, getCurrentAccount } from './web3/contractInteractions';
import { useAuth } from './auth/AuthContext';

export const SearchDialog = ({ searchTextParam, setSearchTextParam, open, onSearch, onClose }) => {
  const classes = useStyles();
  const [searchFieldText, setSearchFieldText] = useState(searchTextParam);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const submitSearch = (overrideValue) => {
    const raw = String(overrideValue != null ? overrideValue : searchFieldText || '').trim();
    setSearchFieldText(raw);
    setSearchTextParam(raw);
    onSearch(raw);
  };

  const handleChange = (event) => {
    setSearchFieldText(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitSearch(event.currentTarget.value);
    }
  };

  return ReactDOM.createPortal(
        <Dialog
          open={open}
          onClose={onClose}
          aria-labelledby="form-dialog-title"
          className={classes.overlay}
          fullScreen={fullScreen}
          PaperProps={{
            style: {
              backgroundColor: '#121212',
              color: 'white',
              border: '1px solid rgba(79, 195, 247, 0.45)',
              borderRadius: fullScreen ? 0 : 10,
              width: fullScreen ? '100%' : 'min(92vw, 420px)',
              maxWidth: fullScreen ? '100%' : 420,
              margin: fullScreen ? 0 : 16,
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.55)',
            }
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch();
            }}
            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
          >
          <DialogTitle id="form-dialog-title" style={{ color: 'white', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 12 }}>
            Search
            <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ backgroundColor: '#121212', color: 'white', paddingTop: 20 }}>
            <TextField
              //inputRef={inputRef}
              autoFocus
              margin="dense"
              id="search"
              type="text"
              fullWidth
              placeholder="e.g. Tether, 0x123456..."
              value={searchFieldText}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#b0b0b0' }}/>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTextParam && (
                      <IconButton
                        aria-label="clear text"
                        onClick={() => setSearchFieldText('')}
                        edge="end"
                        size="small"
                        style={{ color: '#d0d0d0' }}
                      >
                        <ClearIcon  />
                      </IconButton>
                    )}
                  </InputAdornment>
                )
                ,
                style: {
                  color: 'white',
                  backgroundColor: '#000000',
                  borderRadius: 6,
                }
              }}
              inputProps={{
                style: { color: 'white' },
                autoCapitalize: 'none',
                autoCorrect: 'off',
                spellCheck: false,
              }}
            />
          </DialogContent>
          <DialogActions style={{ backgroundColor: '#121212', padding: '12px 16px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Button onClick={onClose} color="primary" style={{ color: '#8fb4ff' }}>
              Cancel
            </Button>
            <Button type="submit" color="primary" style={{ color: '#8fb4ff', fontWeight: 700 }}>
              Search
            </Button>
          </DialogActions>
          </form>
        </Dialog>,
      document.body
    );
};

export const AboutDialog = ({ onClose }) => {
  const classes = useStyles();

  return ReactDOM.createPortal(
    (
      <div className={classes.overlay} onClick={onClose}>
        <div className={classes.scrollDialog} onClick={(e) => e.stopPropagation()} style={{ overflowY: 'auto' }}>
          <AboutContent />
          <Button variant="contained" onClick={onClose} className={`${classes.button} ${classes.closeButton}`}>
            Close
          </Button>
        </div>
      </div>
    ),
    document.getElementById('portal-root')
  );
};

export const AboutContent = () => {
  return (
  <div>
    <h2>Welcome to E3D (Beta)</h2>
    <p>E3D is the first of its kind state-of-the-art cloud-based platform that brings the Ethereum blockchain to life in a 3D interactive graph that breaks down the complex world of blockchain data into an accessible, visual, and immersive experience.
       It is also fun and beautiful. With E3D you get the big picture, and the ability to move around in it, and change it to suit your style.</p>
    
    <h3>Why E3D is Amazing!</h3>
    <ul>
        <li><strong>Immersive 3D Visualization:</strong> Dive into the digital cosmos where each token, smart contract, and transaction is not just a data point, but a visual story. With real-time 3D graphics, you can navigate the Ethereum ecosystem like never before.</li>
        <li><strong>Intuitive User Interface:</strong> Whether you’re a seasoned crypto trader or new to the blockchain space, the E3D UI is designed for effortless interaction. It’s simple to understand, fun to explore, and works seamlessly on AWS-supported servers.</li>
        <li><strong>Dynamic Data Interaction:</strong> Instantly visualize the latest transactions, explore connections between addresses, and interact with the blockchain data in real-time. Click through the graph to learn, analyze, and discover.</li>
    </ul>

    <h3>Features That Empower You!</h3>
    <ul>
        <li><strong>Search and Discover:</strong> Use our powerful search feature to locate addresses or tokens within the Ethereum network, revealing their activity and interconnections.</li>
        <li><strong>Detailed Insights:</strong> Our interface displays intricate details of each transaction, providing you with the information you need at a glance.</li>
        <li><strong>Seamless Navigation:</strong> Click on any list item to auto-navigate through the blockchain universe, focusing on the elements that matter to you.</li>
        <li><strong>Etherscan Integration:</strong> A shift-click on any graph element opens up detailed information on the etherscan.io website for in-depth analysis.</li>
        <li><strong>Take the Tour:</strong> Sit back and take a tour of the tokens. Stop the tour and move through them manually with the left and right arrows</li>
        <li><strong>Auto Refresh:</strong> Turn on Auto Refresh and let the graph update for you. Perfect for the Investor Info wall</li>
    </ul>

    <h3>Exciting Uses Cases!</h3>
    <ul>
        <li><strong>Market Trend Analysis:</strong> Stay ahead of the curve by observing the latest tokens and their interactions within the larger ecosystem.</li>
        <li><strong>Investment Opportunities:</strong> Discover potential investment gems before they hit the major exchanges.</li>
        <li><strong>Educational Tool:</strong> Use our platform as a teaching aid to explain the complexities of blockchain technology in a visually engaging manner.</li>
        <li><strong>Development Resource:</strong> Access a comprehensive tool for developers to analyze smart contracts and blockchain projects.</li>
    </ul>

    <h3>Why Sign Up Now?</h3>
    <ul>
        <li><strong>Early Access:</strong> Be one of the first to experience the future of blockchain interaction.</li>
        <li><strong>Premium Features:</strong> Subscribers gain access to premium features like auto-updates and guided tours, providing an enriched experience.</li>
        <li><strong>Join the Community:</strong> Engage with fellow blockchain enthusiasts and be part of a community that’s shaping the future of on-chain intelligence.</li>
        <li><strong>Future Innovations:</strong> With plans to introduce AI-driven insights and direct wallet integrations, the potential of FutCo is limitless.</li>
    </ul>

    <p>Join E3D today and turn your blockchain exploration into an adventure. Sign up now and be part of the movement that’s making blockchain data engaging, comprehensive, and, most of all, fun!</p>
  </div>
  );
};



export const CookieConsentDialog = ({ onClose, onAccept }) => {
  const classes = useStyles();

  return ReactDOM.createPortal(
    (
      <Dialog open onClose={onClose} aria-labelledby="cookie-consent-title" className={classes.overlay} 
        BackdropProps={{ style: { backgroundColor: 'transparent' } }}>
        <DialogTitle id="cookie-consent-title">
          Cookie Consent
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          We use cookies to improve your experience on our site. By accepting, you agree to our use of cookies.
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Decline
          </Button>
          <Button onClick={onAccept} color="primary">
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    ),
    document.getElementById('portal-root')
  );
};

export const VerifyEmailCodeDialog = ({ onClose, username }) => {
  const classes = useStyles();
  const [code, setCode] = useState(new Array(6).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleDialogClose = (event, reason) => {
    // Avoid accidental close right after mount (eg click-through from the submit button)
    // and avoid closing with ESC while user is entering the code.
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    if (typeof onClose === 'function') onClose(false);
  };
  
  // Reference for focusing between inputs
  const inputRefs = useRef([]);
  
  useEffect(() => {
    // Initialize refs array
    inputRefs.current = inputRefs.current.slice(0, code.length);
    
    // Auto-focus the first input when dialog opens
    if (inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0].focus(), 300);
    }
  }, []);
  
  useEffect(() => {
    // Countdown for resend cooldown
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);
  
  const handleChange = (element, index) => {
    const value = element.value;

    // Check if the input is a number and has only one character
    if (value === '' || (isFinite(value) && value.length <= 1)) {
      let newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      
      // Clear any previous errors
      if (error) setError('');
  
      // Focus next input if the value is not empty
      if (value !== '' && index < 5) {
        inputRefs.current[index + 1].focus();
      }
      // Auto-submit if last digit is entered
      if (value !== '' && index === 5) {
        // Check if all digits are filled
        const allFilled = newCode.every(digit => digit !== '');
        if (allFilled) {
          // Allow a small delay for UI update before submitting
          setTimeout(() => handleSubmit(newCode.join('')), 300);
        }
      }
    }
  };
  
  // Handle backspace navigation
  const handleKeyDown = (e, index) => {
    // If backspace and current field is empty, focus previous field
    if (e.key === 'Backspace' && index > 0 && !code[index]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (manualCode) => {
    // Prevent duplicate submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setError('');
    
    const verificationCode = manualCode || code.join('');
    
    // Validate all digits are entered
    if (verificationCode.length !== 6 || !/^\d{6}$/.test(verificationCode)) {
      setError('Please enter all 6 digits of your verification code');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${SERVER_NAME}/verifyEmailCode`, {
        username: username,
        code: verificationCode
      });

      if (response.data.success) {
        setSuccess(true);
        // Show success message briefly before closing
        setTimeout(() => {
          onClose(true); // Close the dialog with success=true parameter
        }, 2500);
      } else {
        setError(response.data.message || 'Invalid or expired verification code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.response?.data?.message || 'Error during verification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await axios.post(`${SERVER_NAME}/sendEmailCode`, { userId: username });
      setResendCooldown(60); // 60 second cooldown
      alert('A new verification code has been sent to your email.');
    } catch (error) {
      console.error('Error resending code:', error);
      alert('Failed to send a new verification code. Please try again later.');
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text'); // Get pasted value
    if (/^\d{6}$/.test(pasteData)) { // Ensure it's a 6-digit numeric code
      const newCode = [...code]; // Copy the current code state
      pasteData.split('').forEach((digit, index) => {
        if (index < newCode.length) {
          newCode[index] = digit; // Set each digit to the corresponding input
        }
      });
      setCode(newCode); // Update the code state
      
      // Auto-submit on paste
      setTimeout(() => handleSubmit(pasteData), 300);
    }
  };
  
  return ReactDOM.createPortal(
    (
      <Dialog 
        open 
        onClose={success ? null : handleDialogClose} 
        disableEscapeKeyDown
        aria-labelledby="verify-email-title" 
        className={classes.overlay}
        PaperProps={{
          style: {
            backgroundColor: '#121212',
            color: 'white',
            maxWidth: '450px',
            width: '100%'
          }
        }}
      >
        <DialogTitle id="verify-email-title" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Email Verification</span>
            <IconButton 
              aria-label="close" 
              style={{ color: 'white', padding: '8px' }} 
              onClick={() => onClose(false)} 
              disabled={isSubmitting || success}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ fontSize: '64px', margin: '0 0 20px' }}>✓</div>
              <h2 style={{ margin: '0 0 10px', color: '#4caf50' }}>Email Verified!</h2>
              <p>Your email has been successfully verified.</p>
            </div>
          ) : (
            <>
              <p style={{ marginTop: 0, marginBottom: '20px', color: '#e0e0e0' }}>
                Please enter the 6-digit verification code sent to <strong>{username}</strong>
              </p>
              
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                margin: '24px 0'
              }}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    disabled={isSubmitting || success}
                    style={{
                      width: '45px',
                      height: '50px',
                      fontSize: '24px',
                      textAlign: 'center',
                      border: `2px solid ${error ? '#f44336' : 'rgba(255,255,255,0.3)'}`,
                      borderRadius: '4px',
                      backgroundColor: '#1a1a2e',
                      color: 'white',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      caretColor: '#4fc3f7',
                      appearance: 'textfield',
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none'
                    }}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
              
              {error && (
                <p style={{ color: '#f44336', margin: '16px 0', fontSize: '0.9rem', textAlign: 'center' }}>
                  {error}
                </p>
              )}
              
              <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.9rem', color: '#aaaaaa' }}>
                Didn't receive the code?{' '}
                <button 
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isSubmitting}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: resendCooldown > 0 ? '#666666' : '#4fc3f7',
                    textDecoration: 'underline',
                    cursor: resendCooldown > 0 ? 'default' : 'pointer',
                    padding: '0',
                    font: 'inherit',
                    outline: 'none'
                  }}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {!success && (
            <Button 
              onClick={() => handleSubmit()} 
              color="primary" 
              variant="contained"
              disabled={isSubmitting || code.some(digit => digit === '') || success}
              style={{ 
                backgroundColor: '#4fc3f7',
                color: '#121212',
                fontWeight: 'bold',
                minWidth: '120px'
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} style={{ color: '#121212' }} />
              ) : (
                'Verify'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    ),
    document.getElementById('portal-root')
  );
};

// SignupDialog has been moved to its own file - SignupDialog.js

  export function LoginDialog({ open, onClose, onLogin, insidePortal = false, standalone = false, setShowSignup }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [showPassword, setShowPassword] = useState(false); // State for password visibility toggle
    const classes = useStyles();
    
    // Add global styles to fix autocomplete background issues
    useEffect(() => {
      // Create a style element to handle autofill background color
      const styleEl = document.createElement('style');
      styleEl.setAttribute('id', 'login-autofill-style');
      styleEl.innerHTML = `
        /* Override browser's autofill styles */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #000000 inset !important;
          -webkit-text-fill-color: white !important;
          caret-color: white !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        /* Firefox autofill fix */
        input:-moz-autofill,
        input:-moz-autofill:focus {
          background-color: #000000 !important;
          color: white !important;
          box-shadow: 0 0 0 1000px #000000 inset !important;
        }
        /* Edge/IE autofill fix */
        input:-ms-input-placeholder {
          color: #a0a0a0 !important;
        }
      `;
      document.head.appendChild(styleEl);
      
      // Clean up the style element when component unmounts
      return () => {
        const existingStyle = document.getElementById('login-autofill-style');
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }, []);
    
    // Function to toggle password visibility
    const handleTogglePasswordVisibility = () => {
      setShowPassword((prevShowPassword) => !prevShowPassword);
    };
    
    // Get login function from auth context
    const { login } = useAuth();
    
    const handleLogin = async (e) => {
      if (e) {
        e.preventDefault();
      }
      
      if (!username || !password) {
        setLoginError('Please enter both email and password');
        return;
      }
      
      setIsSubmitting(true);
      setLoginError('');
      
      try {
        console.log('Attempting login with AuthContext', { username, serverUrl: SERVER_NAME });
        
        // Standard login request
        console.log(`Sending login request to ${SERVER_NAME}/login with email:`, username);
        const response = await axios.post(
          `${SERVER_NAME}/login`,
          { email: username, password },
          { withCredentials: true }
        );
        
        console.log('Got login response:', response.status, response.data);
        
        if (response.status == 200) {
          console.log('Login successful', response.data);
          
          // Close the login dialog first to avoid state conflicts
          onClose();
          
          // Extract the correct user ID from the response
          const userID = response.data.user?.userid || response.data.user?._id || username;
          console.log('Setting user context with ID:', userID);
          
          // Then update the global auth context with user info
          login({
            username: username,
            userID: userID,
            userAddress: response.data.user?.userAddress || response.data.user?.address || '',
            roles: Array.isArray(response.data.user?.roles) ? response.data.user.roles : [],
            subscribeNewsletter: response.data.user?.subscribeNewsletter
          });
          
          // Call the onLogin callback if provided
          if (onLogin) {
            onLogin(username, password);
          }
        } else {
          const message = response.data.message || 'Login failed. Please try again.';
          console.error('Login response error:', message);
          setLoginError(message);
        }
      } catch (error) {
        console.error('Login error:', error?.response?.data || error.message || error);
        
        // Handle unverified email case
        if (error?.response?.status === 403 && 
            error?.response?.data?.needsVerification && 
            error?.response?.data?.email) {
          
          console.log('Account needs verification:', error.response.data.email);
          
          // Close the login dialog
          onClose();
          
          // Show the verification dialog
          handleSendVerificationCode(error.response.data.email);
          
        } else if (error?.response?.status === 401) {
          setLoginError('Invalid email or password. Please try again.');
        } else {
          setLoginError('Login failed. Please check your credentials and try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    };
    
    // Helper function to handle sending verification code
    const handleSendVerificationCode = async (email) => {
      try {
        // Send verification code to the email
        await axios.post(`${SERVER_NAME}/sendEmailCode`, { userId: email });
        console.log('Verification code sent to:', email);
        
        // Show the email verification dialog
        const verifyEmailRoot = document.createElement('div');
        verifyEmailRoot.id = 'verify-email-root';
        document.body.appendChild(verifyEmailRoot);
        
        // Import the required components
        const ReactDOM = require('react-dom');
        
        ReactDOM.render(
          <VerifyEmailCodeDialog 
            username={email} 
            onClose={(verified) => {
              ReactDOM.unmountComponentAtNode(verifyEmailRoot);
              document.body.removeChild(verifyEmailRoot);
              
              if (verified) {
                // Verification succeeded; allow the normal UI flow to continue without a browser alert.
              }
            }} 
          />,
          verifyEmailRoot
        );
      } catch (error) {
        console.error('Error sending verification code:', error);
        alert('Failed to send verification code. Please try again later.');
      }
    };
    
    // Navigate to signup page or show signup dialog
    const navigateToSignup = () => {
      // Remember the email that the user has already entered
      localStorage.setItem('pendingSignupEmail', username);
      
      onClose();
      
      // Check if we have the setShowSignup prop
      if (setShowSignup) {
        // Use the provided function to show the signup dialog
        setShowSignup(true);
      } else if (standalone) {
        // For standalone mode, navigate to signup page
        window.location.href = '/signup';
      } else if (window.setGlobalShowSignup) {
        // Use the global function if available
        window.setGlobalShowSignup(true);
      } else {
        // Fallback to direct navigation
        window.location.href = '/signup';
      }
    };
  
    // Create custom styles for input fields
    const customStyles = {
      input: {
        '& .MuiOutlinedInput-root': {
          backgroundColor: '#000000',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#4fc3f7',
          },
          // Target autofilled inputs
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
            WebkitTextFillColor: 'white !important',
            caretColor: 'white !important',
            borderRadius: '4px',
            transition: 'background-color 5000s ease-in-out 0s',
          },
          '& input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
            WebkitTextFillColor: 'white !important',
          },
          '& input:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
            WebkitTextFillColor: 'white !important',
          },
          // Firefox specific styles
          '& input::-moz-selection': {
            backgroundColor: '#4fc3f7',
            color: 'white',
          },
          // Edge/IE specific styles
          '& input::selection': {
            backgroundColor: '#4fc3f7',
            color: 'white',
          },
        },
      },
    };
    
    // Create custom dialog styling
    const dialogStyles = {
      paper: {
        backgroundColor: '#121212',
        borderRadius: '8px',
        boxShadow: '0px 3px 15px rgba(0, 0, 0, 0.5)',
        maxWidth: '400px',
        width: '100%',
        margin: '16px',
        border: '1px solid rgba(79, 195, 247, 0.3)', // Light blue border
      },
      title: {
        color: 'white',
        fontSize: '24px',
        fontWeight: '500',
        padding: '16px 24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      },
      closeButton: {
        position: 'absolute',
        right: '12px',
        top: '12px',
        color: 'white',
      },
      content: {
        backgroundColor: '#121212',
        padding: '24px',
        paddingTop: '16px',
        paddingBottom: '16px',
        minWidth: '300px',
      },
      textField: {
        marginBottom: '16px',
        marginTop: '8px',
        backgroundColor: '#121212',
        borderRadius: '4px',
      },
      textFieldInput: {
        backgroundColor: '#000000',
        color: 'white',
        borderRadius: '4px',
      },
      actions: {
        backgroundColor: '#121212',
        padding: '16px 24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      },
      button: {
        padding: '8px 16px',
        fontWeight: '500',
        textTransform: 'none',
        borderRadius: '4px',
      },
      cancelButton: {
        color: '#a0a0a0',
      },
      loginButton: {
        color: '#4fc3f7',
        fontWeight: 'bold',
      },
      signupSection: {
        backgroundColor: '#1A1A1A',
        padding: '16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
      },
      signupText: {
        cursor: 'pointer',
        margin: 0,
        color: '#4fc3f7',
        textAlign: 'center',
        fontSize: '14px',
        padding: '4px',
      },
      errorMessage: {
        color: '#f44336',
        marginBottom: '16px',
        fontSize: '14px',
        padding: '8px 12px',
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
      },
    };
    
    // Always render in the DOM, let the Material UI Dialog handle open/close state
    // This is important for proper transitions and animations
    const dialogContent = (
      <Dialog 
        open={open} 
        onClose={onClose} 
        aria-labelledby="form-dialog-title" 
        className={classes.overlay}
        PaperProps={{
          style: dialogStyles.paper
        }}
      >
        <DialogTitle id="form-dialog-title" style={dialogStyles.title}>
          Sign In
          <IconButton 
            aria-label="close" 
            onClick={onClose}
            style={dialogStyles.closeButton}
          >
            <CloseIcon style={{ color: 'white' }} />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <DialogContent style={dialogStyles.content}>
            {loginError && (
              <div style={dialogStyles.errorMessage}>
                <FaTimes style={{ marginRight: '8px', color: '#f44336' }} />
                {loginError}
              </div>
            )}
            
            <TextField
              autoFocus
              id="username"
              name="email"
              autoComplete="email"
              label="Email Address"
              type="email"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputLabelProps={{
                style: { color: '#a0a0a0' },
                shrink: true,
              }}
              InputProps={{
                style: dialogStyles.textFieldInput,
                classes: { notchedOutline: classes.notchedOutline },
                inputProps: {
                  style: { backgroundColor: '#000000', color: 'white' },
                },
              }}
              disabled={isSubmitting}
              style={{...dialogStyles.textField, ...customStyles.input}}
              variant="outlined"
            />
            <TextField
              id="password"
              name="password"
              autoComplete="current-password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputLabelProps={{
                style: { color: '#a0a0a0' },
                shrink: true,
              }}
              InputProps={{
                style: dialogStyles.textFieldInput,
                classes: { notchedOutline: classes.notchedOutline },
                inputProps: {
                  style: { backgroundColor: '#000000', color: 'white' },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      style={{ color: '#a0a0a0' }}
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={isSubmitting}
              style={{...dialogStyles.textField, ...customStyles.input}}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions style={dialogStyles.actions}>
            <Button 
              onClick={onClose} 
              style={{...dialogStyles.button, ...dialogStyles.cancelButton}}
            >
              CANCEL
            </Button>
            <Button 
              type="submit" 
              style={{...dialogStyles.button, ...dialogStyles.loginButton}}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} style={{ color: '#4fc3f7', marginRight: '10px' }} />
                  Signing in...
                </>
              ) : 'LOGIN'}
            </Button>
          </DialogActions>
        </form>
        <div style={dialogStyles.signupSection}>
          <p 
            onClick={navigateToSignup}
            style={dialogStyles.signupText}
          >
            Don't have an account? Sign up here
          </p>
        </div>
      </Dialog>
    );
    
    // Only use createPortal if we're not already inside another portal
    return insidePortal ? dialogContent : ReactDOM.createPortal(dialogContent, document.body);
  };

  export const LogoutConfirmDialog = ({ open, onClose, onLogout }) => {
    const classes = useStyles();
    
    // Use the logout function from auth context
    const { logout } = useAuth();
    
    const handleLogout = () => {
      // Call the context logout function to clear auth state globally
      logout();
      
      // Also call the component's onLogout if provided
      if (onLogout) {
        onLogout();
      }
      
      // Close the dialog
      onClose();
    };
    
    // Create custom dialog styling for logout
    const dialogStyles = {
      paper: {
        backgroundColor: '#121212',
        borderRadius: '8px',
        boxShadow: '0px 3px 15px rgba(0, 0, 0, 0.5)',
        maxWidth: '360px',
        width: '100%',
        margin: '16px',
        border: '1px solid rgba(79, 195, 247, 0.3)', // Light blue border
        overflow: 'hidden',
      },
      title: {
        color: 'white',
        fontSize: '24px',
        fontWeight: '500',
        padding: '16px 24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      },
      closeButton: {
        position: 'absolute',
        right: '12px',
        top: '12px',
        color: 'white',
      },
      content: {
        backgroundColor: '#121212',
        padding: '24px 36px', // Increased horizontal padding from 24px to 36px
        color: 'white',
        fontWeight: '400',
        fontSize: '16px',
      },
      actions: {
        backgroundColor: '#121212',
        padding: '16px 24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      },
      button: {
        padding: '8px 16px',
        fontWeight: '500',
        textTransform: 'none',
        borderRadius: '4px',
        minWidth: '80px',
      },
      cancelButton: {
        color: '#a0a0a0',
      },
      logoutButton: {
        color: '#4fc3f7',
        fontWeight: 'bold',
      },
      messageText: {
        margin: '8px 0',
        lineHeight: '1.5',
        paddingLeft: '10px',
        paddingRight: '10px',
        textAlign: 'center',
      },
    };

    // Use the portal root if available, otherwise fall back to document.body
    const portalElement = document.getElementById('portal-root') || document.body;
    
    return ReactDOM.createPortal(
        <Dialog 
          open={open} 
          onClose={onClose} 
          aria-labelledby="logout-dialog-title" 
          className={classes.overlay}
          PaperProps={{
            style: dialogStyles.paper
          }}
        >
          <DialogTitle id="logout-dialog-title" style={dialogStyles.title}>
            Log Out
            <IconButton 
              aria-label="close" 
              onClick={onClose}
              style={dialogStyles.closeButton}
            >
              <CloseIcon style={{ color: 'white' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent style={dialogStyles.content}>
            <p style={dialogStyles.messageText}>Are you sure you want to log out?</p>
          </DialogContent>
          <DialogActions style={dialogStyles.actions}>
            <Button 
              onClick={onClose} 
              style={{...dialogStyles.button, ...dialogStyles.cancelButton}}
            >
              CANCEL
            </Button>
            <Button 
              onClick={handleLogout}
              style={{...dialogStyles.button, ...dialogStyles.logoutButton}}
            >
              LOG OUT
            </Button>
          </DialogActions>
        </Dialog>,
      portalElement
    );
  };

  export const MembersOnlyDialog = ({ onClose, onSignup, onLogin }) => {
    const classes = useStyles();
  
    return ReactDOM.createPortal(
      <Dialog open={true} onClose={onClose} aria-labelledby="form-dialog-title" className={classes.overlay}>
        <DialogTitle id="form-dialog-title" style={{ color: 'white' }}>
          Members Only Area
          <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
            <CloseIcon style={{ color: 'white' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ color: 'white', backgroundColor: '#121212' }}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <FaCircle style={{ fontSize: '48px', color: '#4fc3f7', marginBottom: '15px' }} />
            <h2 style={{ color: 'white' }}>Free Subscription Feature</h2>
            <p style={{ color: '#a0a0a0', marginBottom: '20px' }}>
              This feature is available to free and premium members only. Please sign in or create an account to continue.
            </p>
          </div>
        </DialogContent>
        <DialogActions style={{ backgroundColor: '#121212', justifyContent: 'space-between', padding: '16px 24px' }}>
          <Button onClick={onLogin} style={{ color: '#4fc3f7' }}>
            Sign In
          </Button>
          <Button onClick={onSignup} color="primary" variant="contained" style={{ backgroundColor: '#4fc3f7' }}>
            Create Account
          </Button>
        </DialogActions>
      </Dialog>,
      document.body
    );
  };
